/**
 * CancelInstanceDialog - Modal de confirmação para cancelar a assinatura de
 * uma instância individual (Stripe). Suporta cancelamento imediato ou ao
 * final do período pago.
 *
 * ARQUITETURA:
 * - Usa addon-cancel Edge Function via supabase.functions.invoke
 * - Requer tenant_addon_id para identificar a assinatura
 *
 * @version 2.0.0
 * RELEASE: 2026-06-29 v2.0.0 - Padronização visual premium replicando o
 *                              layout do AddInstanceDialog (header com
 *                              gradient + ícone badge destrutivo, corpo com
 *                              cards sutis e footer `bg-muted/10`). Migrado
 *                              de AlertDialog para Dialog padrão. Sem
 *                              alterações em lógica/queries/mutations.
 * RELEASE: 2026-03-05 v1.0.0 - Versão inicial.
 */
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  AlertTriangle,
  Loader2,
  MessageCircle,
  XCircle,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import type { Instance } from '@/hooks/useInstances';

const logger = createLogger('CancelInstanceDialog');

interface CancelInstanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instance: Instance | null;
  tenantId: string;
  onSuccess?: () => void;
}

export function CancelInstanceDialog({
  open,
  onOpenChange,
  instance,
  tenantId,
  onSuccess,
}: CancelInstanceDialogProps) {
  const queryClient = useQueryClient();
  const [cancelImmediately, setCancelImmediately] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /** Executa o cancelamento via Edge Function addon-cancel */
  const handleConfirmCancel = async () => {
    if (!instance?.tenant_addon_id) {
      toast.error('Esta instância não possui assinatura vinculada para cancelar.');
      return;
    }

    setIsSubmitting(true);
    try {
      logger.info('Cancelando instância', {
        instanceId: instance.id,
        tenantAddonId: instance.tenant_addon_id,
        cancelImmediately,
      });

      const { data, error } = await supabase.functions.invoke('addon-cancel', {
        body: {
          tenant_id: tenantId,
          tenant_addon_id: instance.tenant_addon_id,
          instance_id: instance.id,
          cancel_immediately: cancelImmediately,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success(
        cancelImmediately
          ? 'Instância cancelada imediatamente.'
          : 'Cancelamento agendado para o final do período.',
      );

      await queryClient.invalidateQueries({ queryKey: ['instances', tenantId] });
      await queryClient.invalidateQueries({ queryKey: ['tenant-addons', tenantId] });
      await queryClient.invalidateQueries({ queryKey: ['resource-limits', tenantId] });

      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      logger.error('Erro ao cancelar instância', { error });
      toast.error(
        error instanceof Error ? error.message : 'Erro ao cancelar instância',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!instance) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg p-0 gap-0 overflow-hidden flex flex-col"
        hideCloseButton={false}
      >
        {/* ===== HEADER PREMIUM (tom destrutivo) ===== */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-destructive/5 via-destructive/3 to-transparent">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-destructive/10">
                <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
              </div>
              Cancelar instância?
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Esta ação irá cancelar a assinatura desta instância no provedor de
              pagamento.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* ===== CORPO ===== */}
        <div className="px-6 py-4 space-y-3 max-h-[60vh] overflow-y-auto">
          {/* Card de contexto */}
          <div className="rounded-lg border p-3 flex items-center gap-3 bg-muted/20">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
              <MessageCircle className="h-5 w-5 text-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground truncate">
                {instance.instance_name}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {instance.provider_display_name}
              </p>
            </div>
            {instance.inbox_name && (
              <Badge
                variant="outline"
                className="text-[10px] font-normal text-muted-foreground border-border"
              >
                {instance.inbox_name}
              </Badge>
            )}
          </div>

          {/* Aviso de impacto */}
          {instance.inbox_name && (
            <div className="rounded-lg border border-dashed p-4 text-sm text-foreground">
              A inbox{' '}
              <span className="font-semibold">"{instance.inbox_name}"</span>{' '}
              perderá seu motor de mensagens após o cancelamento.
            </div>
          )}

          {/* Opção de cancelamento imediato */}
          <div className="flex items-start gap-3 rounded-lg border border-destructive/20 bg-destructive/5 p-3">
            <Checkbox
              id="cancel-immediately"
              checked={cancelImmediately}
              onCheckedChange={(checked) => setCancelImmediately(checked === true)}
              className="mt-0.5"
            />
            <Label
              htmlFor="cancel-immediately"
              className="text-sm cursor-pointer leading-snug"
            >
              <span className="font-medium text-foreground">Cancelar imediatamente</span>
              <br />
              <span className="text-xs text-muted-foreground">
                Se desmarcado, o cancelamento ocorre ao final do período pago.
              </span>
            </Label>
          </div>
        </div>

        {/* ===== FOOTER PREMIUM ===== */}
        <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-3 border-t bg-muted/10">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Voltar
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={handleConfirmCancel}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
            ) : (
              <XCircle className="h-4 w-4 mr-1.5" />
            )}
            Confirmar cancelamento
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
